import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Check, Loader2, Mail, Pencil, Phone, ShieldCheck, User, X } from "lucide-react";
import ProfileSectionCard from "./ProfileSectionCard";
import { fetchDashboardProfile } from "../../redux/dashboardSlice";
import { patchAppUser } from "../../redux/appAuthSlice";
import { resetProfileInfoStatus, updateProfileInfo } from "../../redux/profileSlice";
import {
  formatPhoneDisplay,
  normalizePhoneForApi,
  validateProfileContact,
} from "../../utils/profileHelpers";

const ProfilePersonalPanel = ({ user, userId, onNotify }) => {
  const dispatch = useDispatch();
  const profileInfoStatus = useSelector((s) => s.profile.profileInfoStatus);
  const saving = profileInfoStatus === "loading";

  const [activeField, setActiveField] = useState(null);
  const [editedValue, setEditedValue] = useState("");

  if (!user) return null;

  const contactField = user.email
    ? {
        icon: Mail,
        label: "Email address",
        field: "email",
        value: user.email,
        verified: Boolean(user.isEmailVerified),
      }
    : user.phoneNumber
      ? {
          icon: Phone,
          label: "Phone number",
          field: "phoneNumber",
          value: formatPhoneDisplay(user.phoneNumber),
          verified: null,
        }
      : null;

  const handleEdit = (field, currentValue) => {
    setActiveField(field);
    setEditedValue(currentValue || "");
  };

  const handleCancel = () => {
    setActiveField(null);
    setEditedValue("");
    dispatch(resetProfileInfoStatus());
  };

  const handleSave = async (field) => {
    const validationError = validateProfileContact(field, editedValue);
    if (validationError) {
      onNotify?.("error", "Invalid value", validationError);
      return;
    }

    if (!userId) {
      onNotify?.("error", "Save failed", "Sign in again to update your profile.");
      return;
    }

    const payload = { id: userId };
    if (field === "name") payload.name = editedValue.trim();
    if (field === "email") payload.email = editedValue.trim();
    if (field === "phoneNumber") payload.phoneNumber = normalizePhoneForApi(editedValue.trim());

    const result = await dispatch(updateProfileInfo(payload));

    if (updateProfileInfo.fulfilled.match(result)) {
      const updatedUser = result.payload?.user;
      if (updatedUser) {
        dispatch(patchAppUser(updatedUser));
      }
      await dispatch(fetchDashboardProfile(userId));
      onNotify?.(
        "success",
        "Profile updated",
        result.payload?.message || "Your profile information was updated."
      );
      handleCancel();
      return;
    }

    onNotify?.(
      "error",
      "Save failed",
      String(result.payload || "Could not update profile information.")
    );
  };

  const renderField = ({ icon: Icon, label, field, value, verified }) => {
    const isEditing = activeField === field;

    return (
      <div className={`prof-field${isEditing ? " prof-field-editing" : ""}`} key={field}>
        <div className="prof-field-head">
          <div className="prof-field-label">
            <span className="prof-field-icon" aria-hidden>
              <Icon size={11} />
            </span>
            <em>{label}</em>
          </div>

          <div className="prof-field-actions">
            {verified != null ? (
              <span className={`prof-chip ${verified ? "prof-chip-success" : "prof-chip-muted"}`}>
                {verified ? (
                  <>
                    <ShieldCheck size={10} aria-hidden />
                    Verified
                  </>
                ) : (
                  "Unverified"
                )}
              </span>
            ) : null}

            {isEditing ? (
              <>
                <button
                  type="button"
                  className="prof-chip-btn prof-chip-btn-muted"
                  onClick={handleCancel}
                  disabled={saving}
                >
                  <X size={10} aria-hidden />
                  Cancel
                </button>
                <button
                  type="button"
                  className="prof-chip-btn prof-chip-btn-primary"
                  onClick={() => handleSave(field)}
                  disabled={saving}
                >
                  {saving ? <Loader2 size={10} className="animate-spin" /> : <Check size={10} />}
                  Save
                </button>
              </>
            ) : (
              <button
                type="button"
                className="prof-chip-btn prof-chip-btn-outline"
                onClick={() => handleEdit(field, field === "phoneNumber" ? value : value)}
              >
                <Pencil size={10} aria-hidden />
                Edit
              </button>
            )}
          </div>
        </div>

        {isEditing ? (
          <input
            className="prof-field-input"
            value={editedValue}
            onChange={(event) => setEditedValue(event.target.value)}
            autoFocus
            disabled={saving}
            type={field === "email" ? "email" : field === "phoneNumber" ? "tel" : "text"}
            inputMode={field === "phoneNumber" ? "tel" : undefined}
            placeholder={`Enter ${label.toLowerCase()}`}
          />
        ) : (
          <p className="prof-field-value">{value || "—"}</p>
        )}
      </div>
    );
  };

  return (
    <ProfileSectionCard
      id="account"
      eyebrow="Account"
      title="Personal Information"
      icon={User}
    >
      {renderField({
        icon: User,
        label: "Display name",
        field: "name",
        value: user.name,
        verified: null,
      })}
      {contactField ? renderField(contactField) : null}
    </ProfileSectionCard>
  );
};

export default ProfilePersonalPanel;
